
import { Component } from 'vue-property-decorator';
import WaterfallChartFilterable from '@/components/reports-v3/components/filterables/WaterfallChartFilterable.vue';
import moment from 'moment';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import { PermissionsGroup } from '@/store/models.def';
import { GobiColor } from '@/helpers/color';
import gdbx from '@/store/modules/gdbx';
import { getTableName } from '@/worker/db/sqlHelpers';

@Component({
	components: {
		WaterfallChartFilterable,
	},
})
export default class BpProfitLossBarChart extends FilterWidget {
	public get permissionIds(): PermissionsGroup[] {
		return ['transactions'];
	}

	public filterIds: Array<
		| 'date'
		| 'dateAsOf'
		| 'dateRange'
		| 'stockItems'
		| 'agents'
		| 'customers'
		| 'suppliers'
    | 'projects'
	> = ['dateRange'];

	public xAxisData = [
		'NET REVENUE',
		'COGS',
		'GROSS PROFIT/LOSS',
		'OTHER INCOME',
		'EXPENSES',
		'NET PROFIT/LOSS BEFORE TAX',
	];
	public barType: string[] = [
		'static',
		'float',
		'static',
		'float',
		'float',
		'static',
		'float',
		'static',
	];
	public dataColor: string[] = [
		GobiColor.COLOR1,
		GobiColor.COLOR2,
		GobiColor.COLOR3,
		GobiColor.COLOR4,
		GobiColor.COLOR5,
		GobiColor.COLOR6,
		GobiColor.COLOR7,
	];
	public xAxisValue: number[] = [];
	// public series: echarts.ECharts[] | any = [];

	public get dateFormatted() {
		return this.selectedDateRange;
	}

	public get currencySymbol() {
		return gdbx.currencySymbol;
	}

	public get expensiveHook() {
		const {
			selectedDateRange,
			selectedProjects,
		} = this;
		return JSON.stringify([
			selectedDateRange,
			selectedProjects,
		]);
	}

    public openingStockDateRange(dateRange: [number, number]): [number, number] {
        return [
            moment(dateRange[0]).add(-1, 'month').valueOf(),
            moment(dateRange[1]).add(-1, 'month').valueOf(),
        ];
    }

	public async expensiveCalc() {
		const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
		const txnTable = getTableName('transactions');
		const stockTable = getTableName('stockBalances');
		const range = this.selectedDateRange;
		const openingRange = this.openingStockDateRange(range);

		let projectFilter = '';
		if (this.selectedProjects.length > 0) {
			const list = this.selectedProjects.map((p) => `'${escapeStr(p)}'`).join(',');
			projectFilter = ` AND "PROJECT" IN (${list})`;
		}

		const jobId = this.currentJobId;
		const queryHead = await this._ensureTablesLoaded(['transactions', 'stockBalances']);
		this.checkInterrupted(jobId);

		this.generateLoadingText(0.3);

		const [txnResult, openingResult, closingResult] = await Promise.all([
			queryHead.rawQuery(`
				SELECT
					SUM(CASE WHEN "ACCTYPE" IN ('SL','SA') THEN TRY_CAST("AMOUNT" AS DOUBLE) ELSE 0 END) as net_sales,
					SUM(CASE WHEN "ACCTYPE" = 'CO' THEN TRY_CAST("AMOUNT" AS DOUBLE) ELSE 0 END) as cogs,
					SUM(CASE WHEN "ACCTYPE" IN ('SL','SA','CO') THEN TRY_CAST("AMOUNT" AS DOUBLE) ELSE 0 END) as gross_profit,
					SUM(CASE WHEN "ACCTYPE" = 'OI' THEN TRY_CAST("AMOUNT" AS DOUBLE) ELSE 0 END) as other_income,
					SUM(CASE WHEN "ACCTYPE" = 'EP' THEN TRY_CAST("AMOUNT" AS DOUBLE) ELSE 0 END) as expenses,
					SUM(CASE WHEN "ACCTYPE" IN ('SL','SA','CO','OI','EO','EP') THEN TRY_CAST("AMOUNT" AS DOUBLE) ELSE 0 END) as net_profit
				FROM ${txnTable}
				WHERE TRY_CAST("DOCDATE" AS BIGINT) BETWEEN ${range[0]} AND ${range[1]}
					AND "ACCTYPE" IN ('SL','SA','CO','OI','EO','EP')
					${projectFilter}
			`),
			queryHead.rawQuery(`
				SELECT SUM(TRY_CAST("AMOUNT" AS DOUBLE)) as total
				FROM ${stockTable}
				WHERE TRY_CAST("DOCDATE" AS BIGINT) BETWEEN ${openingRange[0]} AND ${openingRange[1]}
					${projectFilter}
			`),
			queryHead.rawQuery(`
				SELECT SUM(TRY_CAST("AMOUNT" AS DOUBLE)) as total
				FROM ${stockTable}
				WHERE TRY_CAST("DOCDATE" AS BIGINT) BETWEEN ${range[0]} AND ${range[1]}
					${projectFilter}
			`),
		]);
		this.checkInterrupted(jobId);

		const r = txnResult[0] || {};
		const netSalesTotal = Number(r.net_sales) || 0;
		const cogsTotal = Number(r.cogs) || 0;
		const grossProfitTotal = Number(r.gross_profit) || 0;
		const otherIncomeTotal = Number(r.other_income) || 0;
		const expensesTotal = Number(r.expenses) || 0;
		const netProfitTotal = Number(r.net_profit) || 0;

		const openingStocksTotal = Number(openingResult[0] && openingResult[0].total) || 0;
		const closingStocksTotal = Number(closingResult[0] && closingResult[0].total) || 0;

		const actualGrossProfit = grossProfitTotal * -1
			- openingStocksTotal
			+ closingStocksTotal;
		const actualNetProfit = netProfitTotal * -1
			- openingStocksTotal
			+ closingStocksTotal;

		// COGS = Purchases + Opening Stock - Closing Stock
		const actualCOGS = cogsTotal
			+ openingStocksTotal
			- closingStocksTotal;

		this.xAxisValue = [
			netSalesTotal * -1,
			-actualCOGS,
			actualGrossProfit,
			otherIncomeTotal * -1,
			expensesTotal * -1,
			actualNetProfit,
		];

		this.saveHistory('xAxisData', 'xAxisValue');
	}
}
